import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { api, setAuthToken, type User } from '@/lib/api';

// Normalize user to ensure role is always set
const normalizeUser = (user: { id: string; email: string; name: string | null; role?: 'user' | 'admin' }): User => ({
  ...user,
  role: user.role ?? 'user',
});

/**
 * Auth Store
 * 
 * Manages authentication state with single JWT token.
 * Persisted to localStorage.
 */

// ═══════════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════════

export interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; name?: string }) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: () => Promise<void>;
  clearError: () => void;
  initializeAuth: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════════════

export const useAuthStore = create<AuthState>()(
  persist(
    immer((set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // Actions
      setUser: (user) => set((state) => {
        state.user = user;
        state.isAuthenticated = true;
        state.error = null;
      }),
      
      setToken: (token) => {
        setAuthToken(token);
        set((state) => {
          state.token = token;
        });
      },
      
      login: async (email, password) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        
        try {
          const { token, user } = await api.auth.login(email, password);
          
          setAuthToken(token);
          
          set((state) => {
            state.user = normalizeUser(user);
            state.token = token;
            state.isAuthenticated = true;
            state.isLoading = false;
            state.error = null;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set((state) => {
            state.error = message;
            state.isLoading = false;
          });
          throw error;
        }
      },
      
      register: async (data) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        
        try {
          const { token, user } = await api.auth.register(data);
          
          setAuthToken(token);
          
          set((state) => {
            state.user = normalizeUser(user);
            state.token = token;
            state.isAuthenticated = true;
            state.isLoading = false;
            state.error = null;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set((state) => {
            state.error = message;
            state.isLoading = false;
          });
          throw error;
        }
      },
      
      logout: () => {
        setAuthToken(null);
        
        set((state) => {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.error = null;
        });
      },
      
      fetchCurrentUser: async () => {
        const { token } = get();
        if (!token) return;
        
        set((state) => {
          state.isLoading = true;
        });
        
        try {
          const user = await api.auth.me();
          
          set((state) => {
            state.user = normalizeUser(user);
            state.isAuthenticated = true;
            state.isLoading = false;
          });
        } catch {
          // Token invalid, clear auth state
          get().logout();
          set((state) => {
            state.isLoading = false;
          });
        }
      },
      
      clearError: () => set((state) => {
        state.error = null;
      }),
      
      // Initialize auth token from persisted state
      initializeAuth: () => {
        const { token } = get();
        if (token) {
          setAuthToken(token);
        }
      },
    })),
    {
      name: 'bestix-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Re-initialize auth token after rehydration
        if (state?.token) {
          setAuthToken(state.token);
        }
      },
    }
  )
);

// ═══════════════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════════════

export const selectUser = (state: AuthState): User | null => state.user;
export const selectIsAuthenticated = (state: AuthState): boolean => state.isAuthenticated;
export const selectIsAdmin = (state: AuthState): boolean => state.user?.role === 'admin';
export const selectAuthLoading = (state: AuthState): boolean => state.isLoading;
export const selectAuthError = (state: AuthState): string | null => state.error;
export const selectToken = (state: AuthState): string | null => state.token;
